/**
 * Shared material seam.
 *
 * Every asset factory gets its materials from here and NEVER constructs a
 * material inline. This file existed through M0a so that exactly one edit would
 * swap the whole game onto the toon shader — **that edit is this one** (D-004's
 * placeholder exemption ends at M0b). Nothing in `src/models/` changed.
 *
 * The surface kinds are unchanged and still mean what they meant; what they map
 * to is now `src/render/toon.ts`, where each kind bends the shared lighting
 * model in one documented way (wrap, fuzz, glint, wear).
 */

import * as THREE from 'three';
import { toonMaterial, type SurfaceOptions, type SurfaceKind } from '@/render/toon';

export type { SurfaceKind, SurfaceOptions };

// `surfaceKey` is deliberately public batcher metadata, so it cannot establish
// provenance by itself: an asset could copy the string onto an inline material.
// Keep the actual construction evidence private to this seam and let the asset
// contract ask it by object identity.
const surfaceMaterials = new WeakSet<THREE.Material>();

/**
 * A painted surface.
 *
 * `colour` is albedo, in sRGB hex like every palette value in this project. The
 * LIGHT comes from the zone (`src/render/lighting.ts`); the albedo comes from
 * the object. Keeping those apart is what lets one kit tile be placed in five
 * zones and belong in each of them.
 */
export function surface(
  kind: SurfaceKind,
  colour: number,
  options?: SurfaceOptions,
): THREE.Material {
  const material = toonMaterial(kind, colour, options);
  surfaceMaterials.add(material);
  return material;
}

/** True only for a material returned by this module's `surface()` factory. */
export function isSurfaceMaterial(material: THREE.Material): boolean {
  return surfaceMaterials.has(material);
}

/** Recursively dispose an asset's geometries and materials. */
export function disposeAsset(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as Partial<THREE.Mesh>;
    mesh.geometry?.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat?.dispose();
  });
}
